import { loadScriptAssets, loadStyleAssets } from '@merkur/integration';
import { getMerkur } from '@merkur/core';
import { pushIdentity } from '@iva/syncidentity';
import { Circle, Infinite } from 'infinite-circle';

import EmbedEvent from './event/EmbedEvent';
import EmbedScriptEvent from './event/EmbedScriptEvent';
import DiscussionInstanceEvent from './event/DiscussionInstanceEvent';
import scrollToElement from './interface/scrollToElement';
import getWindowViewportRect from './interface/getWindowViewportRect';
import EventEmitter from './interface/EventEmitter';
import { processTokenError } from './Utils';

const WRAPPER_STYLE = 'display: block;overflow: hidden;position: relative;';
const COMPONENT_ROOT_CLASS = 'szn-component-root';

const scrollCircle = new Circle({
	listen: notify => window.addEventListener('scroll', notify),
	unlisten: notify => window.removeEventListener('scroll', notify)
});

const infinite = new Infinite();
infinite.add(scrollCircle);

export default class Discussion extends EventEmitter {
	constructor(instanceId, service, element, config, authorizationTokenProvider) {
		super();

		this.instanceId = instanceId;

		this.isLoaded = false;
		this.loadListener = null;

		this._service = service;
		this._wrapper = element;
		this._config = config;
		this._authorizationTokenProvider = authorizationTokenProvider;

		this.iframe = null;
		this._skinStyleElement = null;

		this._wrapper.innerHTML = '';
		this._wrapper.style = WRAPPER_STYLE;

		this._componentShadowRoot = this._createComponentShadowRoot(this._wrapper);
		this._instanceCircleId = this._registerScrollCircle();
	}

	get onLoad() {
		return this.loadListener;
	}

	set onLoad(onLoad) {
		this.loadListener = onLoad;

		if (onLoad && this.isLoaded) {
			setTimeout(() => {
				if (this.loadListener === onLoad) {
					onLoad();
				}
			}, 0);
		}
	}

	handleDocumentResize() {
		this._updateBoundingRectInIframe(
			getWindowViewportRect(),
			this._getIframeBoundingClientRect()
		);
	}

	[EmbedEvent.REQUEST_SOCIAL_TOKEN]() {
		const lastToken = this._authorizationTokenProvider.lastAuthorizationToken;

		if (lastToken) {
			this.sendEvent(EmbedScriptEvent.LOGIN, { token: lastToken });
		} else if (this._authorizationTokenProvider.isSignedIn()) {
			this._authorizationTokenProvider.getToken((tokenError, token) => {
				this._authorizationTokenProvider.lastAuthorizationToken = token;

				if (token) {
					this.sendEvent(EmbedScriptEvent.LOGIN, { token });
				}

				processTokenError(tokenError);
			});
		}
	}

	[EmbedEvent.CONTENT_CHANGED](payload) {
		this.fire(DiscussionInstanceEvent.CONTENT_CHANGED, payload);
	}

	[EmbedEvent.ERROR]({ error }) {
		this.fire(DiscussionInstanceEvent.ERROR, { error });
	}

	[EmbedEvent.IDENTITY_FWD]({ sid, rusId }) {
		pushIdentity({ sid, rusId });
	}

	[EmbedEvent.LINK_REDIRECT]({ url }) {
		if (this._config.linksInNewTab) {
			window.open(url, '_blank');
		} else {
			window.location.assign(url);
		}
	}

	[EmbedEvent.LOGIN]() {
		this.fire(DiscussionInstanceEvent.LOGIN);
	}

	[EmbedEvent.RESIZE](payload) {
		this.iframe.style.height = payload.height + 'px';

		this._updateBoundingRectInIframe(
			getWindowViewportRect(),
			this._getIframeBoundingClientRect()
		);

		this.fire(DiscussionInstanceEvent.RESIZED, { height: payload.height });
	}

	[EmbedEvent.SCROLL]({ scrollOffset, alignment, durationInMs, cancelOnUserScroll }) {
		if (!this._config.allowScroll) {
			return;
		}

		const onCompleteCallback = () => {
			this.fire(DiscussionInstanceEvent.SCROLLED);
			this.sendEvent(EmbedScriptEvent.SCROLL_DONE);
		};

		scrollToElement({
			element: this.iframe,
			alignment,
			additionalOffset: scrollOffset,
			durationInMs,
			cancelOnUserScroll,
			onCompleteCallback
		});
	}

	[EmbedEvent.SENTINEL_HANDSHAKE_ACK](payload) {
		if (this.instanceId !== payload.sentinel) {
			return;
		}

		this.isLoaded = true;
		this.fire(DiscussionInstanceEvent.LOADED);

		if (this.onLoad) {
			this.onLoad();
		}
	}

	[EmbedEvent.SKIN_COLORS_FWD](colorTheme) {
		if (this._skinStyleElement) {
			return;
		}

		let cssVariables = '';

		for (const variable in colorTheme) {
			if (!colorTheme[variable]) {
				continue;
			}

			const cssVariable = variable
				.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2')
				.toLowerCase();

			cssVariables += `--szn--skin-${cssVariable}: ${colorTheme[variable]};`;
		}

		if (!cssVariables.length) {
			return;
		}

		this._skinStyleElement = document.createElement('style');
		this._skinStyleElement.innerHTML = `:host { ${cssVariables} }`;

		this._componentShadowRoot.appendChild(this._skinStyleElement);
	}

	[EmbedEvent.OPEN_OAUTH_LOGIN]() {
		try {
			window.SznOauthTokenProvider.signIn(isSignInDialogShown => {
				if (!isSignInDialogShown) {
					this.sendEvent(EmbedScriptEvent.OAUTH_FAIL);
				}
			});
		} catch (error) {
			console.error('discusssion oauth login failed');
			this.sendEvent(EmbedScriptEvent.OAUTH_FAIL);
		}
	}

	[EmbedEvent.WIDGET]({ widget, data }) {
		const widgetUrl = new URL(`${this._config.widgetUrl}/${widget}`);

		Object.keys(data).forEach(key => widgetUrl.searchParams.append(key, data[key]));

		return fetch(widgetUrl)
			.then(response => response.json())
			.then(data => {
				Object.assign(data.props, {
					containerSelector: `.${COMPONENT_ROOT_CLASS}`,
					sendEvent: (type, payload = {}) => this.sendEvent(type, payload)
				});

				return data;
			})
			.then(async widgetProperties => {
				// load widget assets
				await loadScriptAssets(widgetProperties.assets);
				await loadStyleAssets(widgetProperties.assets, this._componentShadowRoot);

				// create instance of merkur widget
				const widget = await getMerkur().create(widgetProperties);

				// alive merkur widget
				return widget.mount(this._componentShadowRoot);
			});
	}

	loadDiscussion(canonicalString, metaData, analyticsData, settings = {}) {
		let oauthSettings = {};
		if (window && !!window.SznOauthTokenProvider) {
			oauthSettings = { oauthLogin: true };
		}

		const discussionUrl = this._constructIframeUrl(['discussion', canonicalString], {
			'discussion--metaData': metaData,
			'page--analyticsData': analyticsData,
			'page--settings': Object.assign({}, oauthSettings, settings)
		});

		this._createIframe(discussionUrl);
	}

	loadThread(commentId, analyticsData) {
		const threadUrl = this._constructIframeUrl(['thread', commentId], {
			'page--analyticsData': analyticsData
		});

		this._createIframe(threadUrl);
	}

	sendEvent(type, payload = {}) {
		if (!this.iframe || !this.iframe.contentWindow) {
			return;
		}

		this.iframe.contentWindow.postMessage(
			{
				sentinel: this.instanceId,
				type,
				payload
			},
			'*'
		);
	}

	unload() {
		scrollCircle.unregister(this._instanceCircleId);

		this._destroyIframe();
	}

	_createComponentShadowRoot(wrapper) {
		const shadowHost = document.createElement('div');
		shadowHost.className = COMPONENT_ROOT_CLASS;

		wrapper.appendChild(shadowHost);

		//IE does no support `attachShadow` method. So for IE we consider regular root as shadow host
		try {
			return shadowHost.attachShadow({ mode: 'closed' });
		} catch (e) {
			return shadowHost;
		}
	}

	_createIframe(srcUrl) {
		if (!this.iframe) {
			this.iframe = document.createElement('iframe');

			this._wrapper.appendChild(this.iframe);
		}

		this.iframe.scrolling = 'no';
		this.iframe.style = 'display: block;position: relative;width: 100%;';
		this.iframe.src = srcUrl;

		return this.iframe;
	}

	/**
	 * Creates url to embedded website with given params.
	 *
	 * @param {Array} pathParams
	 * @param {Object<string, *>} queryStringParams
	 * @returns {string}
	 */
	_constructIframeUrl(pathParams = [], queryStringParams = {}) {
		pathParams.unshift(this._service);
		queryStringParams['sentinel'] = this.instanceId;

		const path = pathParams.map(param => encodeURIComponent(param)).join('/');
		const queryString = Object.keys(queryStringParams)
			.filter(
				param =>
					typeof queryStringParams[param] !== 'undefined' &&
					queryStringParams[param] !== null
			)
			.map(param => {
				let value = queryStringParams[param];

				if (typeof value === 'object') {
					value = JSON.stringify(value);
				}

				return `${param}=${encodeURIComponent(value)}`;
			});

		return `${this._config.embedUrl}/${path}?${
			queryString.length ? queryString.join('&') : ''
		}`;
	}

	_destroyIframe() {
		if (this._wrapper) {
			this._wrapper.innerHTML = '';
			this.iframe = null;
		}
	}

	/**
	 * Returns bounding client rect of the instance iframe
	 *
	 * @returns {DOMRect}
	 */
	_getIframeBoundingClientRect() {
		if (!this.iframe) {
			return {};
		}

		const iframeClientRect = this.iframe.getBoundingClientRect();

		// MS Edge v18 fix
		// post message with payload type ClientRect results into error 80700019
		// we have to copy that into plain object
		return {
			bottom: iframeClientRect.bottom,
			height: iframeClientRect.height,
			left: iframeClientRect.left,
			right: iframeClientRect.right,
			top: iframeClientRect.top,
			width: iframeClientRect.width
		};
	}

	_registerScrollCircle() {
		let lastBoundingRect = '';

		return scrollCircle.register({
			read: () => ({
				window: getWindowViewportRect(),
				iframe: this._getIframeBoundingClientRect()
			}),
			write: ({ payload }) => {
				let boundingRectString;
				const { window, iframe } = payload;

				if ((boundingRectString = JSON.stringify(iframe)) === lastBoundingRect) {
					return;
				}

				this._updateBoundingRectInIframe(window, iframe);
				lastBoundingRect = boundingRectString;
			}
		});
	}

	/**
	 * Sends event with iframe bounding rect into the iframe
	 */
	_updateBoundingRectInIframe(window, iframe) {
		this.sendEvent(EmbedScriptEvent.BOUNDING_RECT_CHANGE, { window, iframe });
	}
}
